import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import nonSubscriptionPurchaseHelpers from '../../helpers/nonSubscriptionPurchase';
import { addMessage } from '../../state/actions/messagesActions';

import { ApplicationState } from '../../types/state/storeTypes';
import { User } from '../../types/api/UsersTypes';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import { NonSubPriceBlockProps, NonSubPriceBlockStateProps } from '../../types/components/collections/NonSubPriceBlock';
import { CollectionResponse } from '../../types/api/CollectionsIndexTypes';

import AddToCart from './AddToCart';
import SalePriceBeforeDiscount from '../shared/SalePriceBeforeDiscount';

import { usePricingLogic } from '../../hooks/usePricingLogic';

const NonSubPriceBlock: React.FC<NonSubPriceBlockProps> = (props) => {
  const { collection, className, onPurchased, onOwnershipDataLoaded, user, subscription, mode, authenticated } = props;

  const [price, setPrice] = useState<number>();
  const [originalPrice, setOriginalPrice] = useState<number>();
  const [businessPrice, setBusinessPrice] = useState<number>();
  const [fetchingPrice, setFetchingPrice] = useState<boolean>(false);

  const { fetchCollectionPriceData, calculatePriceBeforeDiscount, calculateBusinessPriceDiscount } = nonSubscriptionPurchaseHelpers;
  const dispatch = useDispatch();

  const fetchPrice = async () => {
    setFetchingPrice(true);
    try {
      const pricesData = await fetchCollectionPriceData(collection as CollectionResponse, user);
      const mostExpensiveVariant = pricesData.sort(
        (v1, v2) => parseFloat(v2.price) - parseFloat(v1.price)
      )[0];
      setPrice(parseFloat(mostExpensiveVariant.price));
      setBusinessPrice(mostExpensiveVariant.credit_based_dollar_prices_licenses.business_price);
      setOriginalPrice(calculatePriceBeforeDiscount(parseFloat(mostExpensiveVariant.price)));
      setFetchingPrice(false);
    } catch (e) {
      setPrice(undefined);
      dispatch(
        addMessage({
          type: 'error',
          body: e.message || 'An error has occured.'
        })
      );
      setFetchingPrice(false);
    }
  };

  useEffect(() => {
    if (isFreeProduct || authenticated === undefined) return;
    fetchPrice();
  }, [
    user?.min_license_id,
    user?.team?.licensee_profile?.id,
    subscription?.license?.id,
    authenticated,
  ]);

  const {
    isLegacyCredit,
    isFreeProduct
  } = usePricingLogic({
    item: collection,
    onPurchased: () => onPurchased(),
    onOwnershipDataLoaded: (ownership: any) => onOwnershipDataLoaded(ownership),
    user,
    subscription
  });

  const showPrice = () => {
    return !isFreeProduct && !!price;
  };

  const renderBusinessPrice = (): React.ReactElement => {
    const discountedPrice = calculateBusinessPriceDiscount(businessPrice);
    if (!discountedPrice) return (
      <span className='font-bold text-white'>${businessPrice}</span>
    );
    return (
      <span className='font-bold text-white'>
        <SalePriceBeforeDiscount priceBeforeDiscount={businessPrice} className="!text-12 !leading-[12px]" />
        <span>{' '}${discountedPrice}</span>
      </span>
    )
  }

  const UnknownLicenseCashPriceElement = () => (
    !subscription && !user.min_license_id && (
      <div className='flex flex-col'>
        <span className='text-a-light-blue text-12'>Starting at</span>
        <span className="flex flex-row items-center gap-1">
          <SalePriceBeforeDiscount priceBeforeDiscount={originalPrice} />
          <span className='font-bold text-32 leading-36'>${price.toFixed(2)}</span>
        </span>
        <span className='text-a-light-gray text-light text-14 flex flex-row gap-1 items-center'>for individuals and{' '}{renderBusinessPrice()}{' '}for businesses</span>
      </div>
    )
  );

  const KnownLicenseCashPriceElement = () => (
    !subscription &&
    <span className="flex flex-row items-center gap-1">
      <SalePriceBeforeDiscount priceBeforeDiscount={originalPrice} />
      <span className='font-bold text-32 leading-36'>${~~price}</span>
    </span>
  );

  const isPurchaseOptionsMode = mode === 'purchase-options-modal';

  return (
    <div className={`w-full flex flex-col gap-[20px] ${className || ''}`} cy-test-id="price-block">
      {!isLegacyCredit && (
        <>
          {isPurchaseOptionsMode || showPrice() && (
            user?.min_license_id || user?.team?.licensee_profile?.license_type?.id ?
              <KnownLicenseCashPriceElement /> :
              <UnknownLicenseCashPriceElement />
          )}
          <AddToCart collection={collection} />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: ApplicationState): NonSubPriceBlockStateProps => ({
  user: state.auth.user as User,
  subscription: state.auth.subscription as SubscriptionDetail,
  authenticated: state.auth.authenticated,
});

export default connect(mapStateToProps)(NonSubPriceBlock);
